/*
 * (C) 2025 BeyondDemise, All Rights Reserved.
 *
 * AAD (Additional Authenticated Data) construction for the vault envelope.
 *
 * The AAD is a deterministic, typed-field byte concatenation rather than a
 * JSON object. This sidesteps the canonical-JSON problem entirely: two
 * implementations producing different byte representations of the same
 * header (whitespace, key ordering) would compute different AADs and fail
 * to verify each other. Fixed-length integers + length-prefixed strings
 * have one byte representation, full stop. Same pattern as Tink, libsodium
 * secretstream, Saltpack.
 *
 * Two AADs are derived from the same header bytes:
 *
 *   AAD_WRAP = HEADER_NO_WRAPPED_KEY || 0x01
 *   AAD_DATA = HEADER_NO_WRAPPED_KEY || wrapped_key_len_be16 || wrapped_key || 0x02
 *
 * The wrap step's AAD cannot include the wrappedKey itself (it's the
 * output of that step). The data step's AAD CAN, and does, so an attacker
 * can't rebind a wrappedKey from a different envelope into this one.
 * The trailing domain byte (0x01 / 0x02) ensures the two AAD layers can
 * never collide even by accident.
 */

const MAGIC_BYTES = new Uint8Array([0x42, 0x44, 0x56, 0x31]); // "BDV1"
const DOMAIN_WRAP = 0x01;
const DOMAIN_DATA = 0x02;

/** Project-defined algorithm IDs. Stored in the AAD as `be8`. */
export const KDF_ID_ARGON2ID = 0x01;
export const CIPHER_ID_XCHACHA20_POLY1305 = 0x10;

/**
 * Header inputs to the AAD builders. All byte fields are raw `Uint8Array`,
 * not base64 — encoding/decoding belongs to the schema layer.
 */
export interface AadHeader {
  version: number;
  envelopeId: string;
  kdf: {
    algorithmId: number;
    salt: Uint8Array;
    memoryKiB: number;
    iterations: number;
    parallelism: number;
  };
  cipher: {
    algorithmId: number;
    nonce: Uint8Array;
  };
  wrapNonce: Uint8Array;
  counter: number;
}

const u8 = (n: number): Uint8Array => Uint8Array.of(n & 0xff);

const u16be = (n: number): Uint8Array =>
  Uint8Array.of((n >>> 8) & 0xff, n & 0xff);

const u32be = (n: number): Uint8Array =>
  Uint8Array.of(
    (n >>> 24) & 0xff,
    (n >>> 16) & 0xff,
    (n >>> 8) & 0xff,
    n & 0xff
  );

const u64be = (n: number): Uint8Array => {
  // Counter is a JS number; safe up to 2^53. Encode via BigInt for upper bytes.
  const big = BigInt(n);
  const out = new Uint8Array(8);
  for (let i = 7; i >= 0; i--) {
    out[i] = Number((big >> BigInt(8 * (7 - i))) & 0xffn);
  }
  return out;
};

const concat = (...parts: Uint8Array[]): Uint8Array => {
  const total = parts.reduce((s, p) => s + p.length, 0);
  const out = new Uint8Array(total);
  let offset = 0;
  for (const p of parts) {
    out.set(p, offset);
    offset += p.length;
  }
  return out;
};

const lenPrefix8 = (b: Uint8Array): Uint8Array => concat(u8(b.length), b);
const lenPrefix16 = (b: Uint8Array): Uint8Array => concat(u16be(b.length), b);

/**
 * The header bytes shared by both AADs. Excludes the wrappedKey (which is
 * the output of the wrap step) and any ciphertext.
 *
 * @throws if any field violates a length boundary that the layout cannot
 *   represent (envelopeId > 64 KiB UTF-8, salt > 255 B, nonce > 255 B,
 *   counter < 0).
 */
const buildHeaderPrefix = (header: AadHeader): Uint8Array => {
  if (header.counter < 0) {
    throw new Error('counter must be non-negative');
  }
  const envelopeIdBytes = new TextEncoder().encode(header.envelopeId);
  if (envelopeIdBytes.length > 0xffff) {
    throw new Error('envelopeId UTF-8 must be ≤ 65535 bytes');
  }
  if (header.kdf.salt.length > 0xff) {
    throw new Error('kdf.salt must be ≤ 255 bytes');
  }
  if (header.cipher.nonce.length > 0xff) {
    throw new Error('cipher.nonce must be ≤ 255 bytes');
  }
  if (header.wrapNonce.length > 0xff) {
    throw new Error('wrapNonce must be ≤ 255 bytes');
  }

  return concat(
    MAGIC_BYTES,
    u16be(header.version),
    lenPrefix16(envelopeIdBytes),
    u8(header.kdf.algorithmId),
    u32be(header.kdf.memoryKiB),
    u32be(header.kdf.iterations),
    u32be(header.kdf.parallelism),
    lenPrefix8(header.kdf.salt),
    u8(header.cipher.algorithmId),
    lenPrefix8(header.cipher.nonce),
    lenPrefix8(header.wrapNonce),
    u64be(header.counter)
  );
};

/**
 * Build the AAD for the wrap step (vault key encryption under KEK).
 * Domain byte 0x01 is appended.
 */
export const buildAadWrap = (header: AadHeader): Uint8Array =>
  concat(buildHeaderPrefix(header), u8(DOMAIN_WRAP));

/**
 * Build the AAD for the data step (VaultContents encryption under vault key).
 * Includes the wrapped key bytes so an attacker can't swap a wrappedKey
 * from a different envelope. Domain byte 0x02 is appended.
 */
export const buildAadData = (
  header: AadHeader,
  wrappedKey: Uint8Array
): Uint8Array => {
  if (wrappedKey.length > 0xffff) {
    throw new Error('wrappedKey must be ≤ 65535 bytes');
  }
  return concat(
    buildHeaderPrefix(header),
    lenPrefix16(wrappedKey),
    u8(DOMAIN_DATA)
  );
};

/** Exposed for tests that want to assert the prefix structure directly. */
export const __testing = {
  MAGIC_BYTES,
  DOMAIN_WRAP,
  DOMAIN_DATA,
  buildHeaderPrefix,
};
